import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchDashboardData,
  testRPCEndpoint,
  recoverRPCEndpoint,
  getRPCRecoveryEvents,
  configureRPCAlerts,
  configureRPCRecovery,
  getAllRPCs,
  getRPCById,
  createRPC,
  updateRPC,
  deleteRPC,
  bulkDeleteRPCs,
  bulkTestRPCs
} from '../api';
import type { RPCListResponse, RPCQueryParams } from '../types/rpc';

// Define types for dashboard data
export interface SystemHealth {
  healthPercentage: number;
  activeRPCs: number;
  totalRPCs: number;
}

export interface QueueMetrics {
  waitingJobs: number;
  processingRate: string;
  avgProcessingTime: string;
}

export interface DashboardData {
  systemHealth: SystemHealth;
  queueMetrics: QueueMetrics;
  networks?: Record<string, any>;
  recommendations?: Array<any>;
}

/**
 * Hook for fetching RPC dashboard data
 * @param refreshInterval Interval in milliseconds to refresh data (default: 60000)
 * @returns Query result with dashboard data
 */
export const useDashboardData = (refreshInterval = 60000) => {
  return useQuery({
    queryKey: ['dashboardData'],
    queryFn: fetchDashboardData,
    refetchInterval: refreshInterval,
    staleTime: refreshInterval * 0.9
  });
};

/**
 * Hook for testing an RPC endpoint
 * @returns Mutation for testing an RPC endpoint
 */
export const useTestRPC = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ url, network }: { url: string, network: string }) =>
      testRPCEndpoint(url, network),
    onSuccess: () => {
      // Invalidate both dashboard data and RPC list to refresh the UI
      queryClient.invalidateQueries({ queryKey: ['dashboardData'] });
      queryClient.invalidateQueries({ queryKey: ['allRPCs'] });
    }
  });
};

/**
 * Hook for recovering an RPC endpoint
 * @returns Mutation for recovering an RPC endpoint
 */
export const useRecoverRPC = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => recoverRPCEndpoint(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboardData'] });
    }
  });
};

/**
 * Hook for fetching RPC recovery events
 * @param network Optional network filter
 * @param limit Maximum number of events to return
 * @param refreshInterval Interval in milliseconds to refresh data (default: 300000)
 * @returns Query result with recovery events
 */
export const useRPCRecoveryEvents = (
  network?: string,
  limit: number = 50,
  refreshInterval = 300000
) => {
  return useQuery({
    queryKey: ['rpcRecoveryEvents', network, limit],
    queryFn: () => getRPCRecoveryEvents(network, limit),
    refetchInterval: refreshInterval
  });
};

/**
 * Hook for configuring RPC alerts
 * @returns Mutation for configuring RPC alerts
 */
export const useConfigureRPCAlerts = () => {
  return useMutation({
    mutationFn: (settings: any) => configureRPCAlerts(settings)
  });
};

/**
 * Hook for configuring RPC auto-recovery
 * @returns Mutation for configuring RPC auto-recovery
 */
export const useConfigureRPCRecovery = () => {
  return useMutation({
    mutationFn: (settings: any) => configureRPCRecovery(settings)
  });
};

/**
 * Hook for fetching all RPC endpoints
 * @param params Query parameters
 * @param options Query options
 * @returns Query result with RPC endpoints
 */
export const useAllRPCs = (
  params?: RPCQueryParams,
  options?: any
) => {
  return useQuery<RPCListResponse>({
    queryKey: ['allRPCs', params],
    queryFn: () => getAllRPCs(params),
    ...options
  });
};

/**
 * Hook for fetching RPC endpoint by ID
 * @param id RPC endpoint ID
 * @returns Query result with RPC endpoint details
 */
export const useRPCById = (id: string) => {
  return useQuery({
    queryKey: ['rpc', id],
    queryFn: () => getRPCById(id),
    enabled: !!id
  });
};

/**
 * Hook for creating RPC endpoint
 * @returns Mutation for creating RPC endpoint
 */
export const useCreateRPC = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => createRPC(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allRPCs'] });
      queryClient.invalidateQueries({ queryKey: ['dashboardData'] });
    }
  });
};

/**
 * Hook for updating RPC endpoint
 * @returns Mutation for updating RPC endpoint
 */
export const useUpdateRPC = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => updateRPC(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['allRPCs'] });
      queryClient.invalidateQueries({ queryKey: ['rpc', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['dashboardData'] });
    }
  });
};

/**
 * Hook for deleting RPC endpoint
 * @returns Mutation for deleting RPC endpoint
 */
export const useDeleteRPC = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteRPC(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allRPCs'] });
      queryClient.invalidateQueries({ queryKey: ['dashboardData'] });
    }
  });
};

/**
 * Hook for bulk deleting RPC endpoints
 * @returns Mutation for bulk deleting RPC endpoints
 */
export const useBulkDeleteRPCs = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string[]) => bulkDeleteRPCs(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allRPCs'] });
      queryClient.invalidateQueries({ queryKey: ['dashboardData'] });
    }
  });
};

/**
 * Hook for bulk testing RPC endpoints
 * @returns Mutation for bulk testing RPC endpoints
 */
export const useBulkTestRPCs = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (rpcIds: string[]) => bulkTestRPCs(rpcIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allRPCs'] });
      queryClient.invalidateQueries({ queryKey: ['dashboardData'] });
    }
  });
};
